import React from "react";

import {
  createAccordionItems,
  isFootprintReadOnly,
  useGetCarbonProgramByFarmId,
  useGetFootprintingCropFootprint,
  useGetPracticeCrops,
  useNavigate,
  useTranslation,
  useWasAutofilled,
} from "utils";

import { FormFlowScreen, FormPage } from "components";

import { AutofillBanner } from "pages/app/footprinting/components/AutofillBanner";
import { CropFertilizersForm } from "pages/app/footprinting/components/formFlows/cropFlow/CropFertilizersForm";
import { CropFieldSelectionForm } from "pages/app/footprinting/components/formFlows/cropFlow/CropFieldSelectionForm";
import { CropGeneralForm } from "pages/app/footprinting/components/formFlows/cropFlow/CropGeneralForm";
import { CropGettingStartedForm } from "pages/app/footprinting/components/formFlows/cropFlow/CropGettingStartedForm";
import { CropIrrigationForm } from "pages/app/footprinting/components/formFlows/cropFlow/CropIrrigationForm";
import { CropPlantProtectionForm } from "pages/app/footprinting/components/formFlows/cropFlow/CropPlantProtectionForm";
import { CropSeedingForm } from "pages/app/footprinting/components/formFlows/cropFlow/CropSeedingForm";
import { CropTillageForm } from "pages/app/footprinting/components/formFlows/cropFlow/CropTillageForm";
import { CropTransportForm } from "pages/app/footprinting/components/formFlows/cropFlow/CropTransportForm";
import { CropCommentForm } from "pages/app/footprinting/components/formFlows/shared/CommentForm";

interface CropFlowProps {
  farmId: string;
  cropId: string;
  year: number;
  initialStep?: number;
  editMode?: boolean;
  enablePageTransitions?: boolean;
}

const cropFormCategories = {
  fieldWork: "footprinting:footprintingLandingPage.assessmentCategories.crop.fieldWork",
};

export const CropFormFlow = ({ farmId, cropId, year, initialStep, editMode, enablePageTransitions }: CropFlowProps) => {
  const { t } = useTranslation("footprintingCrop");
  const navigate = useNavigate();
  const cropProgressQuery = useGetFootprintingCropFootprint(farmId, cropId, year);
  const readOnly = isFootprintReadOnly(cropProgressQuery.data);
  const carbonProgramQuery = useGetCarbonProgramByFarmId(farmId);
  const wasAutofilled = useWasAutofilled("crop", farmId, year, cropId);
  const { data: allCrops } = useGetPracticeCrops({ enabled: true });

  const cropName = allCrops?.find((crop) => crop.id === cropId)?.name;

  const getPageStatus = (pageKey: string) => {
    if (cropProgressQuery.data?.pagesCompleted.includes(pageKey)) return "complete";
    if (cropProgressQuery.data?.pagesIncomplete.includes(pageKey)) return "incomplete";
    return null;
  };

  const cropFlowSteps: FormPage[] = [
    {
      navigationTitle: t("footprinting:navigation.crop.gettingStarted"),
      key: CropGettingStartedForm.Key,
      status: getPageStatus(CropGettingStartedForm.Key),
      pageSubtitle: t("cropGettingStartedForm.subTitle"),
      body: () => <CropGettingStartedForm primaryDataId={cropProgressQuery.data?.footprint?.id} cropId={cropId} />,
      accordionFooter: wasAutofilled ? <AutofillBanner /> : undefined,
    },
    {
      navigationTitle: t("footprinting:navigation.crop.general"),
      key: CropGeneralForm.Key,
      status: getPageStatus(CropGeneralForm.Key),
      pageSubtitle: t("cropGeneralForm.subTitle"),
      body: () => <CropGeneralForm readOnly={readOnly} primaryDataId={cropProgressQuery.data?.footprint?.id} farmId={farmId} cropId={cropId} year={year} />,
      accordionItems: createAccordionItems(t, "footprintingCrop", "cropGeneral"),
    },
    {
      navigationTitle: t("footprinting:navigation.crop.fields"),
      key: CropFieldSelectionForm.Key,
      status: getPageStatus(CropFieldSelectionForm.Key),
      pageSubtitle: t("cropFieldSelection.info"),
      body: () => (
        <CropFieldSelectionForm readOnly={readOnly} primaryDataId={cropProgressQuery.data?.footprint?.id} farmId={farmId} cropId={cropId} year={year} />
      ),
      accordionItems: createAccordionItems(t, "footprintingCrop", "cropFields"),
    },
    {
      navigationTitle: t("footprinting:navigation.crop.tillage"),
      navigationCategory: t(cropFormCategories.fieldWork),
      key: CropTillageForm.Key,
      status: getPageStatus(CropTillageForm.Key),
      pageSubtitle: t("cropTillageForm.info"),
      body: () => <CropTillageForm readOnly={readOnly} primaryDataId={cropProgressQuery.data?.footprint?.id} farmId={farmId} cropId={cropId} year={year} />,
      accordionItems: createAccordionItems(t, "footprintingCrop", "cropTillage"),
    },
    {
      navigationTitle: t("footprinting:navigation.crop.seeding"),
      navigationCategory: t(cropFormCategories.fieldWork),
      key: CropSeedingForm.Key,
      status: getPageStatus(CropSeedingForm.Key),
      pageSubtitle: t("cropSeedingForm.info"),
      body: () => <CropSeedingForm readOnly={readOnly} primaryDataId={cropProgressQuery.data?.footprint?.id} farmId={farmId} cropId={cropId} year={year} />,
      accordionItems: createAccordionItems(t, "footprintingCrop", "cropSeeding"),
    },
    {
      navigationTitle: t("footprinting:navigation.crop.fertilizers"),
      navigationCategory: t(cropFormCategories.fieldWork),
      key: CropFertilizersForm.Key,
      status: getPageStatus(CropFertilizersForm.Key),
      pageSubtitle: t("cropFertilizerForm.info"),
      body: () => <CropFertilizersForm readOnly={readOnly} primaryDataId={cropProgressQuery.data?.footprint?.id} farmId={farmId} cropId={cropId} year={year} />,
      accordionItems: createAccordionItems(t, "footprintingCrop", "cropFertilizers"),
    },
    {
      navigationTitle: t("footprinting:navigation.crop.plantProtection"),
      navigationCategory: t(cropFormCategories.fieldWork),
      key: CropPlantProtectionForm.Key,
      status: getPageStatus(CropPlantProtectionForm.Key),
      pageSubtitle: t("cropPlantProtectionForm.info"),
      body: () => (
        <CropPlantProtectionForm readOnly={readOnly} primaryDataId={cropProgressQuery.data?.footprint?.id} farmId={farmId} cropId={cropId} year={year} />
      ),
      accordionItems: createAccordionItems(t, "footprintingCrop", "cropPlantProtection"),
    },
    {
      navigationTitle: t("footprinting:navigation.crop.irrigation"),
      key: CropIrrigationForm.Key,
      status: getPageStatus(CropIrrigationForm.Key),
      pageSubtitle: t("cropIrrigation.info"),
      body: () => <CropIrrigationForm readOnly={readOnly} primaryDataId={cropProgressQuery.data?.footprint?.id} farmId={farmId} cropId={cropId} year={year} />,
      accordionItems: createAccordionItems(t, "footprintingCrop", "cropIrrigation"),
    },
    {
      navigationTitle: t("footprinting:navigation.crop.transport"),
      key: CropTransportForm.Key,
      status: getPageStatus(CropTransportForm.Key),
      pageSubtitle: t("cropTransportForm.info"),
      body: () => <CropTransportForm readOnly={readOnly} primaryDataId={cropProgressQuery.data?.footprint?.id} farmId={farmId} cropId={cropId} year={year} />,
      accordionItems: createAccordionItems(t, "footprintingCrop", "cropTransport"),
    },
    {
      navigationTitle: t("footprinting:navigation.crop.comment"),
      key: CropCommentForm.Key,
      status: getPageStatus(CropCommentForm.Key),
      pageSubtitle: t("footprinting:commentForm.subTitle"),
      body: () => <CropCommentForm readOnly={readOnly} primaryDataId={cropProgressQuery.data?.footprint?.id} farmId={farmId} cropId={cropId} year={year} />,
    },
  ];

  // Carbon programs can opt out of specific steps via excludedFormSteps. Excluded steps
  // are filtered here so they never render, and the server mirrors this by excluding
  // them from completion stats. To exclude a step, add its FormStepId to the carbon
  // program's excludedFormSteps array in consts/carbonPrograms.ts on the API.
  const excludedSteps = new Set(carbonProgramQuery.data?.farmLevelFootprinting.excludedFormSteps ?? []);
  const enabledSteps = cropFlowSteps.filter((step) => !excludedSteps.has(step.key));

  return (
    <FormFlowScreen
      formPages={enabledSteps}
      initialStep={initialStep}
      completionPercent={cropProgressQuery.data?.completionPct ?? 0}
      // {/* TODO: Use translated crop names based on HF work */}
      navBarTitle={t("footprinting:navigation.crop.navBarTitle", { cropName, year })}
      editMode={editMode}
      onClose={() => navigate("/footprinting", { state: { year } })}
      isLoading={cropProgressQuery.isLoading}
      enablePageTransitions={enablePageTransitions}
    />
  );
};
